import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { DocSchemaEntityType } from "building-types/handles/foreignEntityId";
import type { SettingsFragment } from "building-types/settings/settingsFragment";
import type { LiteralSchema, Schema, ForeignKeySchemaType } from "common/data/schema";
import { DocSchemaLibrary } from "schemas-common/docSchema";
import { docGetCatalogItem } from "../docGetCatalogItem";
import { docGetTableEntity } from "../entity/docGetTableEntity";
import { docEntityTypeToSchemaName } from "./docEntityTypeToSchemaName";
import { ForeignKeyStringFieldCollector } from "./docSchemaForeignKeyStringVisitor";
import { docSchemaNameToEntityType } from "./docSchemaNameToEntityType";
import { SchemaKindPresenceVisitor, ForeignKeyPresenceVisitor } from "./docSchemaPresenceVisitors";
import { detectDiscriminator, resolveIdentifier } from "./docSchemaUnionTools";
import { settingsTypeToSchemaName } from "./settingsTypeToSchemaName";

export function docGetSchemaForEntity(doc: Doc, handle: DocEntityHandle): Schema | undefined {
  let schema: Schema | undefined = undefined;

  if (handle.type === "CATALOG_ITEM") {
    const catalogItem = docGetCatalogItem(doc, handle);

    if (catalogItem) {
      schema = docGetSchemaForEntityType(catalogItem.catalogItemType);
    }
  } else if (handle.type === "SETTINGS") {
    // For SETTINGS entities, get the specific schema based on the settings type field
    // This allows FK extraction to work on the specific settings type instead of the SettingsFragment union
    const entity = docGetTableEntity(doc, handle) as SettingsFragment | undefined;
    if (entity && entity.type in settingsTypeToSchemaName) {
      const specificSchemaName = settingsTypeToSchemaName[entity.type];
      schema = DocSchemaLibrary[specificSchemaName];
    }

    // Fallback to SettingsFragment if we can't determine the specific type
    if (!schema) {
      schema = docGetSchemaForEntityType(handle.type);
    }
  } else {
    schema = docGetSchemaForEntityType(handle.type);
  }

  return schema;
}

export function docGetSchemaForEntityType(entityType: DocSchemaEntityType): Schema | undefined {
  const schemaName = docEntityTypeToSchemaName[entityType];
  return schemaName ? DocSchemaLibrary[schemaName] : undefined;
}

export function docGetEntityTypeForSchema(schema: Schema): DocSchemaEntityType | undefined {
  switch (schema.kind) {
    case "TUPLE":
    case "OBJECT":
    case "MANUAL":
    case "IDENTIFIER":
    case "MAPPED_OBJECT":
    case "ENUM":
    case "UNION":
    case "TYPE_PARAMETER":
    case "INTERSECTION":
    case "INTERFACE":
      return schema.name ? docSchemaNameToEntityType[schema.name] : undefined;
    default:
      return undefined;
  }
}

/**
 * Recursively extracts field names/paths that are defined as foreign key strings in a schema
 */
interface SchemaForeignKeyStringField {
  fieldPath: string;
  candidateTypes: DocEntityType[];
}

const foreignKeyStringFieldsCache = new WeakMap<Schema, SchemaForeignKeyStringField[]>();
const schemaContainsKindCache = new WeakMap<Schema, Map<ForeignKeySchemaType, boolean>>();
const schemaContainsFksCache = new WeakMap<Schema, boolean>();

function resolveSchemaForVisit(schema: Schema): Schema {
  return schema.kind === "IDENTIFIER" && DocSchemaLibrary[(schema as any).name]
    ? DocSchemaLibrary[(schema as any).name]
    : schema;
}

export function schemaExtractForeignKeyStringFields(schema: Schema): SchemaForeignKeyStringField[] {
  const cached = foreignKeyStringFieldsCache.get(schema);
  if (cached) {
    return cached;
  }

  const v = new ForeignKeyStringFieldCollector();
  // Resolve IDENTIFIER indirection at the call site to ensure visitor sees concrete structure
  v.visit(resolveSchemaForVisit(schema));
  const fields = v.fields;
  foreignKeyStringFieldsCache.set(schema, fields);
  return fields;
}

export function docSchemaContains(schema: Schema, type: ForeignKeySchemaType): boolean {
  let byKind = schemaContainsKindCache.get(schema);
  if (!byKind) {
    byKind = new Map();
    schemaContainsKindCache.set(schema, byKind);
  }

  const cached = byKind.get(type);
  if (cached !== undefined) {
    return cached;
  }

  const v = new SchemaKindPresenceVisitor(type);
  v.visit(resolveSchemaForVisit(schema));
  byKind.set(type, v.found);
  return v.found;
}

export function docSchemaContainsFks(schema: Schema): boolean {
  const cached = schemaContainsFksCache.get(schema);
  if (cached !== undefined) {
    return cached;
  }

  const v = new ForeignKeyPresenceVisitor();
  v.visit(resolveSchemaForVisit(schema));
  schemaContainsFksCache.set(schema, v.found);
  return v.found;
}

/**
 * If the given schema is a discriminated union, resolves to the specific branch
 * whose discriminator literal matches the entity's data. This ensures that
 * descriptors are only computed for the matching branch (e.g., the correct
 * parameter type in LocalParameter), avoiding false FK references from
 * other branches.
 *
 * Handles both unions of interfaces and unions of intersections (where the
 * discriminator field lives inside one of the intersection members).
 *
 * Returns the original schema if it is not a discriminated union or no branch matches.
 */

/**
 * Cache of discriminator value → resolved branch schema, keyed by the concrete UNION schema.
 * Avoids re-scanning branch literals for every entity of the same union type.
 */
const resolvedBranchByUnion = new WeakMap<Schema, Map<unknown, Schema>>();

export function resolveSchemaForEntity(schema: Schema, entity: Record<string, unknown>): Schema {
  const resolved = resolveIdentifier(schema);
  if (resolved.kind !== "UNION") {
    return schema;
  }

  const unionTypes = (resolved as any).unionTypes as Schema[];
  const result = detectDiscriminator(unionTypes);
  if (!result) {
    return schema;
  }

  const entityValue = entity[result.field];
  let branchByValue = resolvedBranchByUnion.get(resolved);
  if (!branchByValue) {
    branchByValue = new Map();
    for (const branch of result.branches) {
      branchByValue.set((branch.fields[result.field] as LiteralSchema).value, branch.branchSchema);
    }
    resolvedBranchByUnion.set(resolved, branchByValue);
  }

  return branchByValue.get(entityValue) ?? schema;
}
